/**
 * The description's pictures (docs/SPEC.md §13 3., §30.5; P28-T10): one
 * picture per scene, its captions in, drawn by the film's player in the
 * browser from the version's manifest and zipped there
 * (`frames.ts::zipOfFrames`), then saved as a file. Nothing is drawn on the
 * server and nothing is charged. A scene that cannot be drawn is left out
 * and said.
 */
import { useState } from "react";

import { api } from "../api/client";
import { manifestUrlOf } from "../api/versions";
import { WAYS_OUT, say } from "../copy/outputs";
import { zipOfFrames } from "../film/frames";
import { parseManifest } from "../film/manifest";
import { useFrameWrap } from "../film/useFrame";
import { Button } from "../ui";

/** Saves `blob` as a file named `name` (the browser's download). */
export function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** The zip's file name: the ad and the version it pictures. */
export function zipName(adId: string, version: number): string {
  return `truereel-${adId.slice(0, 8)}-scenes-v${version}.zip`;
}

type State = "idle" | "drawing" | "done" | "failed";

export function FramesZip({
  adId,
  version,
  save = saveBlob,
}: {
  adId: string;
  /** The version described. */
  version: number;
  /** Saves the zip (a test gives its own). */
  save?: (blob: Blob, name: string) => void;
}) {
  const wrap = useFrameWrap();
  const [state, setState] = useState<State>("idle");
  const [missing, setMissing] = useState(0);

  const run = async () => {
    setState("drawing");
    try {
      const manifest = parseManifest(
        await api<unknown>(manifestUrlOf(adId, version)),
      );
      const made = await zipOfFrames(manifest, { wrap });
      save(made.zip, zipName(adId, version));
      setMissing(made.missing.length);
      setState("done");
    } catch {
      setState("failed");
    }
  };

  return (
    <div className="flex flex-col items-start gap-1" data-testid="frames-zip">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={state === "drawing"}
        aria-busy={state === "drawing"}
        onClick={() => void run()}
      >
        {state === "drawing"
          ? WAYS_OUT.elsewhere.zipping
          : WAYS_OUT.elsewhere.zip}
      </Button>
      {state === "done" && missing > 0 ? (
        <p className="m-0 text-xs text-slate-600" role="status">
          {say(WAYS_OUT.elsewhere.zipMissing, { n: missing })}
        </p>
      ) : null}
      {state === "failed" ? (
        <p className="m-0 text-xs text-slate-700" role="alert">
          {WAYS_OUT.elsewhere.zipFailed}
        </p>
      ) : null}
    </div>
  );
}
